import axios from 'axios';
import moment from 'moment';
import { Helpers } from '../helpers/Helpers';
import { ColumnChooserModel } from '../models/common/ColumnChooserModel';
import { ColumnFilterModel } from '../models/common/ColumnFilterModel';

/**
 * Mesai Kayıtları (İnsan Kaynakları > Mesai Kayıtları, QR Mesai Faz 1b, 11.10.2026).
 * Personel depo QR'ı, sürücü araç QR'ı ile giriş / çıkış yapar; yetkili web'den elle ekler ya da çıkışı düzeltir.
 */

export const MESAI_TURLERI = [
    { value: 1, label: 'Personel' },
    { value: 2, label: 'Sürücü' },
];

export const IS_TIPLERI = [
    { value: 1, label: 'Lojistik' },
    { value: 2, label: 'Ambalaj' },
];

export const GUN_TIPLERI = [
    { value: 1, label: 'Hafta içi' },
    { value: 2, label: 'Cumartesi' },
    { value: 3, label: 'Pazar' },
    { value: 4, label: 'Resmi tatil' },
    { value: 5, label: 'Yarım gün' },
];

export const MESAI_DURUMLARI = [
    { value: 1, label: 'Açık' },
    { value: 2, label: 'Tamam' },
    { value: 3, label: 'Eksik çıkış' },
];

/** Kaydın neden kapandığı (QR Mesai Faz 2, 18.10.2026). */
export const KAPANIS_NEDENLERI = [
    { value: 1, label: 'Aynı QR' },
    { value: 2, label: 'Başka QR' },
    { value: 3, label: 'Elle düzeltme' },
    { value: 4, label: 'Eksik çıkış' },
];

export const MESAI_KAYNAKLARI = [
    { value: 1, label: 'QR' },
    { value: 2, label: 'Elle' },
];

export type HavuzDurumu = null | 'BEKLIYOR' | 'ATANDI' | 'YENIDEN';

export interface MesaiSiparisi {
    ambarId: string
    refNo: string
}

export interface MesaiKaydiOzet {
    mesaiKaydiId: string
    tur: number
    /** Nokta adı ya da plaka. */
    noktaAdi: string | null
    isTipi: number | null
    isTipiAdi: string | null
    /** "yyyy-MM-dd" (giriş günü) */
    tarih: string
    /** "HH:mm" */
    girisSaati: string
    cikisSaati: string | null
    normalDk: number
    ekDk: number
    sureDk: number
    durum: number
    durumAdi: string
}

export interface MesaiKaydiItem extends MesaiKaydiOzet {
    kisiId: string
    kisiAdi: string
    turAdi: string
    gunTipiAdi: string | null
    siparisDk: number
    /** Yalnız "Personel Ücretleri" yetkisine; değilse null. */
    personelUcret: number | null
    /** Bu kayıttan doğan sipariş satırlarının KDV hariç toplamı. */
    musteriTutari: number | null
    siparisler: MesaiSiparisi[] | null
    havuzDurumu: HavuzDurumu
    kaynakAdi: string | null
    duzeltenAdi: string | null
    duzeltmeZamani: string | null
    girisMesafe: number | null
    cikisMesafe: number | null
    // ---- QR Mesai Faz 2 (18.10.2026); eski sunucuda gelmeyebilir.
    /** "Depo" / "Ofis" / "Araç". */
    noktaTuru?: string | null
    kapanisNedeni?: number | null
    kapanisNedeniAdi?: string | null
    girisLat?: number | null
    girisLon?: number | null
    cikisLat?: number | null
    cikisLon?: number | null
    /** Kaydın yerleştiği ek mesai talebi. */
    mesaiTalebiId?: string | null
    /** "#12 · 18:00–22:00" */
    talepEtiketi?: string | null
    /** Talebe faturalanan aralık, ör. "18:00–20:15". */
    faturaAraligi?: string | null
    faturaDk?: number | null
    /** Araç kaydında plaka. */
    aracPlaka?: string | null
    /** Liste ekranında satır seçimi (yalnız ekranda). */
    isSelected?: boolean
}

/** Standart liste isteği + tarih aralığı. */
export interface MesaiKaydiListIstegi {
    searchTerm: string
    skip: number
    take: number
    currentPage: number
    sortColumn?: string
    sortDirection?: string
    columnChooser: ColumnChooserModel[] | undefined
    columnFilters: ColumnFilterModel[]
    ids?: string[]
    /** "yyyy-MM-dd" */
    baslangic: string
    /** "yyyy-MM-dd" */
    bitis: string
}

export const initialMesaiKaydiListIstegi = (): MesaiKaydiListIstegi => ({
    searchTerm: '',
    skip: 0,
    take: 50,
    currentPage: 1,
    sortColumn: undefined,
    sortDirection: undefined,
    columnChooser: undefined,
    columnFilters: [],
    baslangic: moment().startOf('month').format('YYYY-MM-DD'),
    bitis: moment().endOf('month').format('YYYY-MM-DD'),
})

export interface MesaiKaydiListSonucu {
    list: MesaiKaydiItem[]
    totalRecordCount: number
}

export interface CikisDuzeltIstegi {
    mesaiKaydiId: string
    /** "HH:mm" */
    cikisSaati: string
    aciklama?: string | null
}

export interface ElleEkleIstegi {
    tur: number
    personnelId?: string | null
    driverId?: string | null
    mesaiNoktasiId?: string | null
    vehicleId?: string | null
    /** "yyyy-MM-dd" */
    tarih: string
    /** "HH:mm" */
    girisSaati: string
    cikisSaati?: string | null
    isTipi?: number | null
    aciklama: string
}

interface Yanit<T> { isSuccess: boolean, message?: string, data: T }

const cagir = async <T>(uc: string, govde: any = {}): Promise<Yanit<T>> => {
    try {
        console.log('SERVICE CALL - MesaiKaydi/' + uc, JSON.stringify(govde));
        const resp = await axios.post(Helpers.getEndpoint() + '/MesaiKaydi/' + uc, govde);
        return Helpers.dataParser(resp);
    } catch (error) {
        throw Helpers.parseErrorMessage(error);
    }
}

export class MesaiKaydiService {
    static getListPagingAndFilter = (istek: MesaiKaydiListIstegi) => cagir<MesaiKaydiListSonucu>('GetListPagingAndFilter', istek);
    static exportList = (istek: MesaiKaydiListIstegi) => cagir<string>('ExportList', istek);
    static cikisDuzelt = (istek: CikisDuzeltIstegi) => cagir<MesaiKaydiItem>('CikisDuzelt', istek);
    static elleEkle = (istek: ElleEkleIstegi) => cagir<MesaiKaydiItem>('ElleEkle', istek);
    static sil = (ids: string[]) => cagir<boolean>('Sil', { ids });
}

/** Dakika → "s:dd" (ör. 150 → "2:30"). */
export const saatDakika = (dk: number | null | undefined) => {
    if (dk == null)
        return '';

    var n = Math.max(0, Math.round(dk));
    var s = Math.floor(n / 60), d = n % 60;

    return s + ':' + (d < 10 ? '0' : '') + d;
}

/** Dakika → ondalık saat metni (ör. 135 → "2,25"). */
export const ondalikSaat = (dk: number | null | undefined) => {
    if (dk == null)
        return '';

    return (Math.round(dk / 60 * 100) / 100).toLocaleString('tr-TR', { maximumFractionDigits: 2 });
}

/** "yyyy-MM-dd" → "dd.MM.yyyy" */
export const trTarih = (t: string | null | undefined) => {
    if (!t)
        return '';

    var p = t.substring(0, 10).split('-');

    return p.length == 3 ? p[2] + '.' + p[1] + '.' + p[0] : t;
}
